/**
 * Step 1 · About You — photos, name, age, gender, bio, ethnicity,
 * body type, diet, pets, politics, orientation, show-me, looking-for,
 * interests, religion, work, education, languages, personality, zodiac,
 * exercise, height, prompts.
 *
 * Extracted from ProfileSetup.jsx during the Feb 2026 refactor. Every
 * data-testid preserved so Playwright suites keep passing.
 */
import { toast } from 'sonner';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Loader2, Plus, ShieldCheck, IdCard, BadgeCheck, ChevronRight, Search } from 'lucide-react';
import ReorderablePhotos from '@/components/ReorderablePhotos';
import PersonaVerifyModal from '@/components/PersonaVerifyModal';
import { formatHeight, heightBounds } from '@/utils/units';
import { PillSelect, PillMulti, PromptsBuilder, inputCls } from './shared';
import VoicePromptSection from './VoicePromptSection';

// Feb 2026 · v2.2.0 — Height picker used inside StepAboutYou. Renders a
// horizontal snap-scrolling ruler with the selected height centered and
// highlighted. Feels wheel-like on mobile (touch-scroll + snap) and works
// as a keyboard/pointer-drag control on desktop. Value flows to
// `profile.height_cm`.
export function HeightWheel({ value, onChange, testId = 'setup-height-wheel' }) {
  const { min, max } = heightBounds();
  const trackRef = useRef(null);
  const initializedRef = useRef(false);
  const options = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  const current = value || 173;   // 5'8" default

  // Center the selected value in the scroll viewport on mount / change.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const idx = options.indexOf(current);
    if (idx < 0) return;
    const child = el.children[idx];
    if (child && typeof child.scrollIntoView === 'function') {
      child.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
    }
    // Ignore programmatic scroll snapping until layout settles.
    initializedRef.current = false;
    const t = setTimeout(() => { initializedRef.current = true; }, 250);
    return () => clearTimeout(t);
  }, [current]);

  // On user scroll, snap the value to whichever tick lands closest to center.
  const onScroll = () => {
    if (!initializedRef.current) return;
    const el = trackRef.current;
    if (!el) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let closestIdx = 0;
    let closestDist = Infinity;
    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i];
      const childCenter = child.offsetLeft + child.clientWidth / 2;
      const dist = Math.abs(center - childCenter);
      if (dist < closestDist) { closestDist = dist; closestIdx = i; }
    }
    const next = options[closestIdx];
    if (next !== value) onChange(next);
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm text-zinc-600">Height (optional)</span>
        <span className="text-[24px] font-bold text-[#0F0F10]" data-testid="setup-height-readout">
          {value ? formatHeight(value) : '—'}
        </span>
      </div>
      <div className="relative h-16 rounded-2xl bg-[#F8F9FA] border border-black/[0.06] overflow-hidden">
        {/* Center indicator line */}
        <div className="pointer-events-none absolute inset-y-1.5 left-1/2 -translate-x-1/2 w-[3px] rounded-full brand-gradient z-10" aria-hidden="true" />
        {/* Snap-scroll track */}
        <div ref={trackRef} onScroll={onScroll}
          className="absolute inset-0 flex items-end gap-0 overflow-x-scroll scroll-smooth"
          style={{ scrollSnapType: 'x mandatory', paddingLeft: '50%', paddingRight: '50%', scrollbarWidth: 'none' }}>
          {options.map((cm) => {
            const totalIn = Math.round(cm / 2.54);
            const isFoot = totalIn % 12 === 0;
            const isHalfFoot = totalIn % 6 === 0;
            const active = cm === value;
            return (
              <div key={cm}
                data-testid={`setup-height-tick-${cm}`}
                className="flex-shrink-0 flex flex-col items-center justify-end pb-2"
                style={{ scrollSnapAlign: 'center', width: '18px' }}>
                <div className={`${isFoot ? 'h-6' : isHalfFoot ? 'h-4' : 'h-2.5'} w-[1.5px] rounded-full ${active ? 'bg-[#EF4444]' : 'bg-black/25'} transition-colors`} />
                {isFoot && (
                  <span className="text-[9px] font-semibold text-[#8A8A93] mt-1 tabular-nums">
                    {Math.floor(totalIn / 12)}&apos;
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 mt-1.5">Slide to set — we&apos;ll display feet/inches or cm based on your unit preference.</p>
    </div>
  );
}

// Feb 27 2026 · Phase 159 · fires an `interest_search_no_result` analytics
// event when the interests search box has no matches for ~800ms. Used by
// StepAboutYou to log gaps in the interest catalog so we can add popular
// missing terms in future releases. Fire-and-forget; never blocks UI.
function NoResultsHint({ query, isEditMode }) {
  useEffect(() => {
    if (!query || !query.trim()) return;
    const timer = setTimeout(() => {
      try {
        const token = localStorage.getItem('session_token');
        if (!token) return;
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/interest-event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            event: 'interest_search_no_result',
            interest: query,   // reused field for query text
            context: isEditMode ? 'edit_profile' : 'onboarding',
          }),
        }).catch(() => {});
      } catch { /* non-critical */ }
    }, 800);
    return () => clearTimeout(timer);
  }, [query, isEditMode]);
  return (
    <p className="text-xs text-zinc-500 italic" data-testid="interests-no-results">
      No interests match "{query}".
    </p>
  );
}

export default function StepAboutYou({
  profile, setProfile,
  fileRef, uploadPhoto, uploading,
  reorderPhotos, removePhotoByPath, getUrl,
  orientationsList, promptsList, promptsCategories = [], interestsList, interestsCategories = [], religionsList,
  industriesList, educationList, languagesList,
  personalityList, zodiacList, exerciseList, politicsList = [],
  onNext,
  isEditMode = false,
}) {
  const navigate = useNavigate();
  const [personaOpen, setPersonaOpen] = useState(false);
  const [personaJustVerified, setPersonaJustVerified] = useState(false);
  const [interestsQuery, setInterestsQuery] = useState('');   // Feb 27 2026 · Phase 159 · Interests search
  const selfieVerified =
    Array.isArray(profile?.badges) && profile.badges.includes('selfie_verified');
  const idVerified =
    personaJustVerified ||
    profile?.verified === true ||
    (Array.isArray(profile?.badges) && profile.badges.includes('verified'));
  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-1">Tell us about yourself</h2>
        <p className="text-zinc-500 text-sm">Be real. Be you.</p>
      </div>

      {/* Photos */}
      <div>
        <label className="text-sm font-medium text-zinc-600 mb-2 block">
          Photos <span className={`ml-1 font-bold ${profile.photos.length >= 3 ? 'text-[#16A34A]' : 'text-[#B91C1C]'}`}>{profile.photos.length}/6</span>
          <span className="text-[11px] text-zinc-500 ml-1">(min 3 required)</span>
        </label>
        <input type="file" ref={fileRef} accept="image/*" onChange={uploadPhoto} className="hidden" />
        <ReorderablePhotos
          photos={profile.photos}
          onReorder={reorderPhotos}
          onRemove={removePhotoByPath}
          getUrl={getUrl}
          maxPhotos={6}
          onAdd={profile.photos.length < 6 ? (
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-photo-btn"
              className="aspect-square rounded-xl border-2 border-dashed border-black/10 hover:border-[#B91C1C]/40 hover:bg-[#B91C1C]/5 flex flex-col items-center justify-center gap-1 transition-all">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[#B91C1C]" /> : <Plus className="w-5 h-5 text-zinc-500" />}
              <span className="text-[10px] text-zinc-500">Add</span>
            </button>
          ) : null}
        />
      </div>

      {/* Verification — optional trust boost. Mirrors the CTAs from
          Profile → Safety so users can add badges from onboarding OR while
          editing. Never gates the Continue button; users can skip and add
          badges later. */}
      <div
        className="rounded-2xl border border-black/[0.06] bg-white p-4"
        data-testid="edit-profile-verification-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <BadgeCheck size={16} className="text-[#1E4FD8]" />
          <span className="text-[11px] font-[800] uppercase tracking-[0.14em] text-[#0F0F10]">
            Verification
          </span>
          <span className="ml-auto text-[10px] font-[700] uppercase tracking-[0.14em] text-[#8A8A93]">
            Optional
          </span>
        </div>
        <p className="text-[12px] text-[#4B4B52] mb-3 leading-relaxed">
          {isEditMode
            ? 'Verified profiles get 3× more matches. Add either — or both — badges.'
            : "Verified profiles get 3× more matches. Add either — or both — later from Profile → Safety, or start now."}
        </p>
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => navigate('/verify')}
            data-testid="edit-profile-row-selfie-verify"
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-black/[0.06] hover:bg-black/[0.02] transition text-left"
          >
            <div className="w-10 h-10 rounded-full bg-[#F4F5F8] flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={18} className="text-[#0F0F10]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-[700] text-[#0F0F10]">Selfie Verification</p>
              <p className="text-[11px] text-[#8A8A93] truncate">
                {selfieVerified ? "You're verified." : 'Match a selfie against your photos'}
              </p>
            </div>
            {selfieVerified ? (
              <BadgeCheck size={16} className="text-[#15803D]" />
            ) : (
              <ChevronRight size={14} className="text-[#8A8A93]" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setPersonaOpen(true)}
            data-testid="edit-profile-row-persona-verify"
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-black/[0.06] hover:bg-black/[0.02] transition text-left"
          >
            <div className="w-10 h-10 rounded-full bg-[rgba(30,79,216,0.08)] flex items-center justify-center flex-shrink-0">
              <IdCard size={18} className="text-[#1E4FD8]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-[700] text-[#0F0F10]">Government ID Verification</p>
              <p className="text-[11px] text-[#8A8A93] truncate">
                {idVerified ? "You're ID-verified — blue badge active." : 'Confirm your legal identity'}
              </p>
            </div>
            {idVerified ? (
              <BadgeCheck size={16} className="text-[#1E4FD8]" />
            ) : (
              <ChevronRight size={14} className="text-[#8A8A93]" />
            )}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-sm text-zinc-600 mb-1.5 block">Display Name *</Label>
          <Input data-testid="setup-name" placeholder="How you'd like to be called" className={inputCls}
            value={profile.display_name} onChange={(e) => setProfile((p) => ({ ...p, display_name: e.target.value }))} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm text-zinc-600 mb-1.5 block">Age *</Label>
            <Input data-testid="setup-age" type="number" placeholder="25" min={18} max={99} className={inputCls}
              value={profile.age} onChange={(e) => setProfile((p) => ({ ...p, age: e.target.value }))} />
          </div>
          <div>
            <Label className="text-sm text-zinc-600 mb-1.5 block">Gender *</Label>
            <Select value={profile.gender} onValueChange={(v) => setProfile((p) => ({ ...p, gender: v }))}>
              <SelectTrigger data-testid="setup-gender" className="rounded-xl bg-black/[0.03] border-black/[0.08] text-white h-auto py-3">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="bg-[#F8F9FA] border-black/[0.08]">
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="non-binary">Non-binary</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-sm text-zinc-600 mb-1.5 block">Bio</Label>
          <Textarea data-testid="setup-bio" placeholder="What makes you, you?" className={`${inputCls} min-h-[100px] resize-none`}
            value={profile.bio} onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))} />
        </div>

        {/* Feb 2026 · Voice prompt recorder. Wired to POST /api/voice-prompt.
            Server transcribes via Whisper and safety-scans the transcript
            before persisting; a rejected clip won't leave a file behind. */}
        <VoicePromptSection
          voicePrompt={profile.voice_prompt}
          onSaved={(v) => setProfile((p) => ({ ...p, voice_prompt: v }))}
        />

        {/*
          Feb 2026 · v2.2.0 — "A bit about you" cluster (physical/demographic).
          Kept to Ethnicity → Height → Body type. Diet/Pets/Politics live
          below in a separate cluster so this section stays focused.
        */}
        <div className="space-y-4 pt-2">
          <div>
            <h3 className="text-[16px] font-bold tracking-tight text-[#0F0F10]">A bit about you</h3>
            <p className="text-[12px] text-[#8A8A93] mt-0.5">Optional — helps others get to know you.</p>
          </div>

          <PillMulti
            label="Ethnicity / Race (optional)"
            subtitle="Select all that apply."
            values={Array.isArray(profile.race) ? profile.race : (profile.race ? [profile.race] : [])}
            onChange={(next) => setProfile((p) => ({ ...p, race: next }))}
            options={['Asian','Black/African Descent','Hispanic/Latino','Middle Eastern','Native American','Pacific Islander','White/Caucasian','Mixed','Other']}
            testId="setup-race"
          />

          <HeightWheel
            value={profile.height_cm}
            onChange={(cm) => setProfile((p) => ({ ...p, height_cm: cm }))}
          />

          <PillSelect label="Body type (optional)" value={profile.body_type} testId="setup-body-type"
            onChange={(v) => setProfile((p) => ({ ...p, body_type: v }))}
            options={['Slim','Athletic','Average','Stocky','Curvy','Plus-size','Muscular']} />
        </div>

        <PillSelect label="Diet (optional)" value={profile.diet} testId="setup-diet"
          onChange={(v) => setProfile((p) => ({ ...p, diet: v }))}
          options={['Omnivore','Vegetarian','Vegan','Pescatarian','Kosher','Halal']} />
        <PillSelect label="Pets (optional)" value={profile.pets} testId="setup-pets"
          onChange={(v) => setProfile((p) => ({ ...p, pets: v }))}
          options={['Dog','Cat','Both','None','Other']} />
        <PillSelect label="Politics (optional)" value={profile.politics} testId="setup-politics"
          onChange={(v) => setProfile((p) => ({ ...p, politics: v }))}
          options={politicsList.length ? politicsList : ['Liberal','Moderate','Conservative','Independent','Apolitical','Other','Prefer not to say']} />

        {/* ─────────────── Orientation ───────────────
            Feb 2026 · v2.2.0 — merged Sexual orientation + gender-based
            discovery preference into one focused section.

            The old "Show me (orientations)" and "Looking for (single
            gender)" widgets were replaced by ONE multi-select "Interested
            in" chip row storing gender tokens (`female` / `male` /
            `nonbinary` / `everyone`) into `show_me_orientations`. This is
            structurally correct — someone's sexual orientation ≠ which
            genders they want to see. Backend `_fits_gender` in
            scoring/compatibility.py already reads `show_me_orientations`
            as gender-or-orientation tokens with `everyone` as a bypass,
            so no schema change was required.

            `looking_for` is mirrored for backwards compat with older
            discovery filters that still key on it. */}
        <div className="space-y-4 pt-2">
          <div>
            <h3 className="text-[16px] font-bold tracking-tight text-[#0F0F10]">Orientation</h3>
            <p className="text-[12px] text-[#8A8A93] mt-0.5">Tell us a little about yourself and who you&apos;d like to meet.</p>
          </div>

          {/* Sexual orientation — single select */}
          <div>
            <Label className="text-sm text-zinc-600 mb-1.5 block">Sexual orientation (optional)</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="setup-orientation">
              {orientationsList.map((o) => {
                const active = profile.sexual_orientation === o.value;
                return (
                  <button key={o.value} type="button" onClick={() => setProfile((p) => ({ ...p, sexual_orientation: active ? '' : o.value }))}
                    data-testid={`setup-orientation-${o.value}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Interested in — multi-select gender preference */}
          <div>
            <Label className="text-sm text-zinc-600 mb-1 block">Interested in</Label>
            <p className="text-[11px] text-zinc-500 mb-2">Pick one or more.</p>
            <div className="flex flex-wrap gap-1.5" data-testid="setup-interested-in">
              {[
                { value: 'female',    label: 'Women' },
                { value: 'male',      label: 'Men' },
                { value: 'nonbinary', label: 'Nonbinary people' },
                { value: 'everyone',  label: 'Everyone' },
              ].map((o) => {
                const cur = profile.show_me_orientations || [];
                const active = cur.includes(o.value);
                const toggle = () => setProfile((p) => {
                  const list = p.show_me_orientations || [];
                  // "Everyone" is a mutually-exclusive shortcut — picking it
                  // clears the other choices; picking any other value clears
                  // "everyone". Keeps the mental model unambiguous.
                  let next;
                  if (o.value === 'everyone') {
                    next = active ? [] : ['everyone'];
                  } else {
                    const without = list.filter((x) => x !== 'everyone');
                    next = active
                      ? without.filter((x) => x !== o.value)
                      : [...without, o.value];
                  }
                  // Mirror to `looking_for` for legacy filters:
                  //   single male   → 'male'
                  //   single female → 'female'
                  //   anything else → 'everyone'
                  let lookingFor = 'everyone';
                  if (next.length === 1 && next[0] === 'male') lookingFor = 'male';
                  else if (next.length === 1 && next[0] === 'female') lookingFor = 'female';
                  return { ...p, show_me_orientations: next, looking_for: lookingFor };
                });
                return (
                  <button key={o.value} type="button" onClick={toggle}
                    data-testid={`setup-interested-in-${o.value}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Interests
            Feb 27 2026 · Phase 159 — Interests library expansion.
            Backend catalog is now ~123 items across 8 categories (see
            INTERESTS_CATEGORIES in routes/compatibility.py). We render:
              1. A search input (case-insensitive, filters flat)
              2. One section per category (labels only — no emojis in pills)
              3. A "Currently on your profile" section for legacy values
                 (e.g. "Pets") that aren't in the new approved catalog,
                 so users can keep or deselect them without data loss.
            Cap = 8, enforced client-side + at profile.py write path. */}
        {(() => {
          const selected     = profile.interests || [];
          const catalogItems = new Set(
            (interestsCategories?.length ? interestsCategories : []).flatMap(c => c.items)
          );
          // Fallback for older clients where interestsCategories didn't ship.
          const cats = interestsCategories?.length
            ? interestsCategories
            : (interestsList?.length ? [{ key: 'all', label: 'Interests', items: interestsList }] : []);
          // Any selected interest that's not in the new catalog gets its own
          // section so the user can still see + deselect it. Empty when the
          // user is fully migrated (majority of accounts).
          const legacySelected = selected.filter(i => !catalogItems.has(i));
          const q = (interestsQuery || '').trim().toLowerCase();
          const matches = (item) => !q || item.toLowerCase().includes(q);
          const toggle = (i) => setProfile((p) => {
            const cur = p.interests || [];
            if (cur.includes(i)) {
              // Fire-and-forget analytics — never blocks UI.
              try {
                const token = localStorage.getItem('session_token');
                if (token) fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/interest-event`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ event: 'interest_removed', interest: i,
                                          context: isEditMode ? 'edit_profile' : 'onboarding' }),
                }).catch(() => {});
              } catch { /* localStorage/fetch may fail — non-critical */ }
              return { ...p, interests: cur.filter((x) => x !== i) };
            }
            if (cur.length >= 8) { toast.error('Max 8 interests'); return p; }
            try {
              const token = localStorage.getItem('session_token');
              if (token) fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/interest-event`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ event: 'interest_added', interest: i,
                                        context: isEditMode ? 'edit_profile' : 'onboarding' }),
              }).catch(() => {});
            } catch { /* non-critical */ }
            return { ...p, interests: [...cur, i] };
          });
          const Chip = (i) => {
            const active = selected.includes(i);
            return (
              <button key={i} type="button"
                data-testid={`interest-chip-${i}`}
                onClick={() => toggle(i)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                {i}
              </button>
            );
          };
          return (
            <div id="section-interests">
              <Label className="text-sm text-zinc-600 mb-1.5 block">
                Interests (optional) <span className="ml-2 text-[#B91C1C] font-semibold" data-testid="interests-counter">{selected.length}/8</span>
              </Label>
              <p className="text-[11px] text-zinc-500 mb-3">Tap up to 8 that feel like you.</p>
              <div className="relative mb-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                <Input
                  data-testid="interests-search"
                  placeholder="Search interests"
                  value={interestsQuery}
                  onChange={(e) => setInterestsQuery(e.target.value)}
                  className="pl-9 rounded-xl bg-white border-black/[0.1] text-[#0F0F10] placeholder:text-zinc-400 text-[13px] h-10"
                />
              </div>
              <div className="space-y-4" data-testid="setup-interests">
                {legacySelected.length > 0 && (
                  <div data-testid="interests-cat-legacy">
                    <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Currently on your profile</p>
                    <div className="flex flex-wrap gap-1.5">
                      {legacySelected.filter(matches).map(Chip)}
                    </div>
                  </div>
                )}
                {cats.map((cat) => {
                  const shown = cat.items.filter(matches);
                  if (!shown.length) return null;
                  return (
                    <div key={cat.key} data-testid={`interests-cat-${cat.key}`}>
                      <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">{cat.label}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {shown.map(Chip)}
                      </div>
                    </div>
                  );
                })}
                {q && cats.every(c => !c.items.some(matches)) && legacySelected.every(i => !matches(i)) && (
                  <NoResultsHint query={interestsQuery} isEditMode={isEditMode} />
                )}
              </div>
            </div>
          );
        })()}

        {/* Religion */}
        <div>
          <Label className="text-sm text-zinc-600 mb-1.5 block">Religion (optional)</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="setup-religion">
            {religionsList.map((r) => {
              const active = profile.religion === r.value;
              return (
                <button key={r.value} type="button" onClick={() => setProfile((p) => ({ ...p, religion: active ? '' : r.value }))}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Work */}
        <div className="space-y-2">
          <Label className="text-sm text-zinc-600 block">Work (optional)</Label>
          <Input data-testid="setup-job-title"
            placeholder={profile.industry === 'Student' ? 'Field of study — e.g. Computer Science' : 'Job title — e.g. Product Designer'}
            maxLength={80}
            value={profile.job_title} onChange={(e) => setProfile((p) => ({ ...p, job_title: e.target.value }))}
            className="rounded-xl bg-white border-black/[0.1] text-[#0F0F10] placeholder:text-zinc-400 px-4 py-3" />
          {/* Feb 2026 · hide Company when Student is selected — "Company"
              doesn't apply to students and the field just adds friction.
              We also clear any stale value so a user who first typed a
              company and then switched to Student doesn't submit stale data. */}
          {profile.industry !== 'Student' && (
            <Input data-testid="setup-company" placeholder="Company (optional)" maxLength={80}
              value={profile.company} onChange={(e) => setProfile((p) => ({ ...p, company: e.target.value }))}
              className="rounded-xl bg-white border-black/[0.1] text-[#0F0F10] placeholder:text-zinc-400 px-4 py-3" />
          )}
          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="setup-industry">
            {industriesList.map((i) => {
              const active = profile.industry === i;
              return (
                <button key={i} type="button"
                  onClick={() => setProfile((p) => ({
                    ...p,
                    industry: active ? '' : i,
                    // Clear stale company when switching TO Student.
                    company: (!active && i === 'Student') ? '' : p.company,
                  }))}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                  {i}
                </button>
              );
            })}
          </div>
        </div>

        {/* Education */}
        <div className="space-y-2">
          <Label className="text-sm text-zinc-600 block">Education (optional)</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="setup-education">
            {educationList.map((e) => {
              const active = profile.education === e.value;
              return (
                <button key={e.value} type="button" onClick={() => setProfile((p) => ({ ...p, education: active ? '' : e.value }))}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                  {e.label}
                </button>
              );
            })}
          </div>
          <Input data-testid="setup-school-name" placeholder="School name (optional)" maxLength={80}
            value={profile.school_name} onChange={(e) => setProfile((p) => ({ ...p, school_name: e.target.value }))}
            className="rounded-xl bg-white border-black/[0.1] text-[#0F0F10] placeholder:text-zinc-400 px-4 py-3" />
        </div>

        {/* Languages */}
        <div>
          <Label className="text-sm text-zinc-600 mb-1.5 block">
            Languages spoken (optional) <span className="ml-2 text-[#B91C1C] font-semibold">{(profile.languages || []).length}/5</span>
          </Label>
          <div className="flex flex-wrap gap-1.5" data-testid="setup-languages">
            {languagesList.map((l) => {
              const active = (profile.languages || []).includes(l);
              return (
                <button key={l} type="button"
                  onClick={() => setProfile((p) => {
                    const cur = p.languages || [];
                    if (active) return { ...p, languages: cur.filter((x) => x !== l) };
                    if (cur.length >= 5) { toast.error('Max 5 languages'); return p; }
                    return { ...p, languages: [...cur, l] };
                  })}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                  {l}
                </button>
              );
            })}
          </div>
        </div>

        {/* Personality */}
        <div>
          <Label className="text-sm text-zinc-600 mb-1.5 block">Personality type (optional)</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="setup-personality-type">
            {personalityList.map((p) => {
              const active = profile.personality_type === p;
              return (
                <button key={p} type="button" onClick={() => setProfile((prof) => ({ ...prof, personality_type: active ? '' : p }))}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                  {p}
                </button>
              );
            })}
          </div>
        </div>

        {/* Social style (Feb 2026) — moved here from the Compatibility step.
            Key stays `social_energy` and value is stored under
            `profile.answers.social_energy` so no data migration is needed;
            legacy answers (introvert / extrovert / ambivert AND the newer
            very_social / small_groups / homebody / mix from the previous
            iteration) still show correctly as the picker resurfaces exact
            matches. */}
        <PillSelect
          label="Social style (optional)"
          value={profile.answers?.social_energy || ''}
          testId="setup-social-style"
          onChange={(v) => setProfile((p) => ({
            ...p,
            answers: { ...(p.answers || {}), social_energy: v },
          }))}
          options={['Introvert', 'Ambivert', 'Extrovert']}
        />

        {/* Zodiac */}
        <div>
          <Label className="text-sm text-zinc-600 mb-1.5 block">Zodiac sign (optional)</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="setup-zodiac-sign">
            {zodiacList.map((z) => {
              const active = profile.zodiac_sign === z;
              return (
                <button key={z} type="button" onClick={() => setProfile((prof) => ({ ...prof, zodiac_sign: active ? '' : z }))}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                  {z}
                </button>
              );
            })}
          </div>
        </div>

        {/* Exercise */}
        <div>
          <Label className="text-sm text-zinc-600 mb-1.5 block">How often do you exercise? (optional)</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="setup-exercise">
            {exerciseList.map((e) => {
              const active = profile.exercise === e.value;
              return (
                <button key={e.value} type="button" onClick={() => setProfile((prof) => ({ ...prof, exercise: active ? '' : e.value }))}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${active ? 'brand-gradient text-white border-transparent' : 'bg-white text-[#4B4B52] border-black/[0.1] hover:bg-black/[0.02]'}`}>
                  {e.label}
                </button>
              );
            })}
          </div>
        </div>

        <PromptsBuilder
          available={promptsList}
          categories={promptsCategories}
          prompts={profile.prompts}
          onChange={(next) => setProfile((p) => ({ ...p, prompts: next }))} />
      </div>

      <button onClick={onNext} data-testid="next-step-1"
        disabled={!profile.display_name || !profile.age || !profile.gender || profile.photos.length < 3}
        className="w-full brand-gradient text-white py-3.5 rounded-xl font-semibold transition-all hover:shadow-[0_0_30px_rgba(255,51,120,0.2)] disabled:opacity-30 active:scale-[0.98]">
        {profile.photos.length < 3
          ? `Add ${3 - profile.photos.length} more photo${3 - profile.photos.length === 1 ? '' : 's'} to continue`
          : (!profile.display_name || !profile.age || !profile.gender)
            ? 'Fill name, age & gender to continue'
            : <>Continue <ArrowRight className="inline ml-2 w-4 h-4" /></>}
      </button>

      <PersonaVerifyModal
        open={personaOpen}
        onClose={() => setPersonaOpen(false)}
        onVerified={() => setPersonaJustVerified(true)}
      />
    </div>
  );
}
